import React, { useCallback, useEffect, useState } from "react";
import {
  clientBillingApi,
  ClientBillingPaymentItem,
  ClientBillingSubscriptionSummary,
} from "../api/clientBillingApi";

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase() === "USD" ? "USD" : currency,
  }).format(cents / 100);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export interface ClientBillingTabProps {
  clientId: string;
  clientName: string;
}

const ClientBillingTab: React.FC<ClientBillingTabProps> = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscription, setSubscription] = useState<ClientBillingSubscriptionSummary | null>(null);
  const [payments, setPayments] = useState<ClientBillingPaymentItem[]>([]);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const [sub, pays] = await Promise.all([
        clientBillingApi.getClientSubscription(clientId),
        clientBillingApi.getClientPayments(clientId),
      ]);
      setSubscription(sub);
      setPayments(pays);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load billing.");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="space-y-6">
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
        <h3 className="text-lg font-semibold text-white">Billing</h3>
        <p className="mt-1 text-sm text-slate-400">
          Subscription and payments for {clientName || "this client"}.
        </p>
      </div>

      {loading && <p className="text-sm text-slate-400">Loading billing…</p>}
      {error && (
        <div className="rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>
      )}

      {!loading && !error && (
        <>
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Subscription</h4>
            {subscription ? (
              <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Plan</dt>
                  <dd className="font-medium text-white">{subscription.planName || "—"}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Amount</dt>
                  <dd className="font-medium text-white">
                    {formatMoney(subscription.amountCents, subscription.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Status</dt>
                  <dd className="font-medium text-slate-200">{subscription.status}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Current period ends</dt>
                  <dd className="font-medium text-slate-200">{formatDate(subscription.currentPeriodEnd)}</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm text-slate-400">No subscription on file for this client.</p>
            )}
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Recent payments</h4>
            {payments.length === 0 ? (
              <p className="mt-3 text-sm text-slate-400">No payments recorded yet.</p>
            ) : (
              <ul className="mt-4 divide-y divide-slate-800">
                {payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-2 py-3 text-sm">
                    <span className="font-medium text-white">{formatMoney(p.amountCents, p.currency)}</span>
                    <span className="text-slate-400">{formatDate(p.paidAt)}</span>
                    <span className="w-full text-xs text-slate-500 sm:w-auto">{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default ClientBillingTab;
